import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  Checkbox,
  useTheme,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import GitHubIcon from '@material-ui/icons/GitHub';
import DnsIcon from '@material-ui/icons/Dns';
import SettingsIcon from '@material-ui/icons/Settings';
import CodeIcon from '@material-ui/icons/Code';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';
import { Entity } from '@backstage/catalog-model';

export function validateDomainName(domain: string): {
  valid: boolean;
  error?: string;
} {
  if (!domain || typeof domain !== 'string') {
    return { valid: false, error: 'Domain must be a non-empty string.' };
  }
  const trimmed = domain.trim().toLowerCase();
  if (!trimmed) {
    return { valid: false, error: 'Domain cannot be empty or whitespace.' };
  }
  if (trimmed.length > 253) {
    return { valid: false, error: 'Domain name cannot exceed 253 characters.' };
  }
  if (trimmed.includes('://')) {
    return {
      valid: false,
      error: 'Domain must not contain scheme (http:// or https://).',
    };
  }
  if (
    trimmed.includes('/') ||
    trimmed.includes(':') ||
    trimmed.includes('?') ||
    trimmed.includes('#')
  ) {
    return {
      valid: false,
      error: 'Domain must not contain port, path, query, or fragment.',
    };
  }
  if (trimmed.includes('*')) {
    return {
      valid: false,
      error: 'Wildcard domains are not permitted in this version.',
    };
  }
  const labels = trimmed.split('.');
  if (labels.length < 2) {
    return {
      valid: false,
      error:
        'Domain must be a fully qualified domain name with at least one dot (e.g. app.example.com).',
    };
  }
  for (const label of labels) {
    if (!label) {
      return {
        valid: false,
        error: 'Domain contains empty label or consecutive dots.',
      };
    }
    if (label.length > 63) {
      return {
        valid: false,
        error: `Domain label "${label}" exceeds 63 characters.`,
      };
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(label)) {
      return {
        valid: false,
        error: `Domain label "${label}" contains invalid characters or starts/ends with a hyphen.`,
      };
    }
  }
  return { valid: true };
}

export function computeExpectedUrl(
  domain?: string,
  serverIp?: string,
  externalPort?: number,
): string {
  if (domain && domain.trim()) {
    return `http://${domain.trim().toLowerCase()}`;
  }
  if (externalPort === 0) return '';
  const ip = serverIp?.trim() || '127.0.0.1';
  const port = externalPort || 80;
  const isIpv6 = ip.includes(':');
  const hostPart = isIpv6 ? `[${ip}]` : ip;
  return port === 80 ? `http://${hostPart}` : `http://${hostPart}:${port}`;
}

export function validateGitBranchName(name: string): {
  valid: boolean;
  error?: string;
} {
  if (!name || typeof name !== 'string' || !name.trim()) {
    return { valid: false, error: 'Branch name cannot be empty.' };
  }
  if (name !== name.trim()) {
    return {
      valid: false,
      error: 'Branch name cannot have leading or trailing whitespace.',
    };
  }
  if (name === '@') {
    return { valid: false, error: 'Branch name cannot be a single "@".' };
  }
  if (name.startsWith('.')) {
    return {
      valid: false,
      error: 'Branch name cannot start with a dot (".").',
    };
  }
  if (name.endsWith('.')) {
    return { valid: false, error: 'Branch name cannot end with a dot (".").' };
  }
  if (name.endsWith('.lock')) {
    return { valid: false, error: 'Branch name cannot end with ".lock".' };
  }
  if (name.startsWith('/') || name.endsWith('/')) {
    return {
      valid: false,
      error: 'Branch name cannot start or end with a slash ("/").',
    };
  }
  if (name.includes('//')) {
    return {
      valid: false,
      error: 'Branch name cannot contain consecutive slashes ("//").',
    };
  }
  if (name.includes('..')) {
    return {
      valid: false,
      error: 'Branch name cannot contain two consecutive dots ("..").',
    };
  }
  if (name.includes('@{')) {
    return { valid: false, error: 'Branch name cannot contain "@{".' };
  }
  if (/[\x00-\x20\x7F\s~^:?*\[\\]/.test(name)) {
    return {
      valid: false,
      error:
        'Branch name contains invalid characters (whitespace, control characters, or ~ ^ : ? * [ \\).',
    };
  }
  return { valid: true };
}

export function extractRepoFromComponent(
  entity: Entity,
): { owner: string; repo: string } | null {
  const annotations = entity.metadata.annotations || {};
  const slug =
    annotations['github.com/project-slug'] || annotations['abc.io/repository'];

  if (slug && typeof slug === 'string' && slug.includes('/')) {
    const parts = slug.trim().split('/');
    if (parts.length === 2 && parts[0] && parts[1]) {
      return { owner: parts[0], repo: parts[1] };
    }
  }

  const locations = [
    annotations['backstage.io/source-location'],
    annotations['backstage.io/managed-by-location'],
  ];

  for (const loc of locations) {
    if (loc && typeof loc === 'string') {
      const clean = loc.replace(/^url:/, '').replace(/\.git$/, '');
      const match = clean.match(
        /github\.com\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_.-]+)/,
      );
      if (match && match[1] && match[2]) {
        return { owner: match[1], repo: match[2] };
      }
    }
  }

  return null;
}

export interface ServerOption {
  id: string;
  name: string;
  provider: string;
  environment: string;
  ip: string;
  runtimes: string[];
}

export interface UserOption {
  entityRef: string;
  displayName: string;
  email?: string | null;
  githubUsername?: string | null;
}

export interface EnvironmentConfigItem {
  runtime?: 'compose' | 'kubernetes';
  serverId: string;
  serverIp?: string;
  serverName?: string;
  sshUser?: string;
  sshPort?: number;
  deployDirectory?: string;
  execution?: 'setup' | 'setup-and-deploy';
  autoBuildOnPush?: boolean;
  domain?: string;
  cpus: number;
  memory: string;
  internalPort: number;
  externalPort: number;
  allowedUsers: string[];
  healthCheckUrl?: string;
}

export interface DevopsEnvironmentSetupValue {
  projectAdminRefs?: string[];
  componentRef?: string;
  componentName?: string;
  componentOwner?: string;
  repository?: string;
  sourceBranch?: string;
  environments?: string[];
  environmentConfigs?: Record<string, EnvironmentConfigItem>;
}

export interface BranchPreviewItem {
  name: string;
  exists: boolean;
  status:
    | 'Will be created'
    | 'Already exists — will be preserved'
    | 'Check failed';
  url?: string;
}

export interface DevopsEnvironmentPickerProps {
  onChange: (value: DevopsEnvironmentSetupValue) => void;
  formData?: DevopsEnvironmentSetupValue;
  required?: boolean;
  rawErrors?: string[];
  idSchema?: {
    $id: string;
  };
  schema?: {
    title?: string;
    description?: string;
  };
}

export function generatePreviewComposeYaml(options: {
  imageHub: string;
  component: string;
  externalPort: number;
  internalPort: number;
  cpus: number;
  memory: string;
}): string {
  return `services:
  app:
    image: ${options.imageHub}/${options.component.toLowerCase()}:\${IMAGE_TAG}
    init: true
    restart: unless-stopped
${
  options.externalPort === 0
    ? ''
    : `    ports:
      - "${options.externalPort}:${options.internalPort}"
`
}
    cpus: ${options.cpus}
    mem_limit: ${options.memory.toLowerCase()}
    pids_limit: 512
    environment:
      TZ: Asia/Ho_Chi_Minh
    logging:
      driver: json-file
      options:
        max-size: "5m"
        max-file: "1"
    security_opt:
      - no-new-privileges:true
    networks:
      - app

networks:
  app:
    driver: bridge`;
}

export const DevopsEnvironmentPickerComponent = (
  props: DevopsEnvironmentPickerProps,
) => {
  const { onChange, formData = {}, rawErrors } = props;
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // 1. Catalog Component selection
  const [components, setComponents] = useState<Entity[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(true);
  const [componentError, setComponentError] = useState<string | null>(null);

  // 2. Server list and User list from abc-api
  const [servers, setServers] = useState<ServerOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [imageHub, setImageHub] = useState<string>('ghcr.io/nhatvh99-oss');

  // 3. Selected Component & Repository
  const selectedComponentRef = formData.componentRef || '';
  const sourceBranch = formData.sourceBranch || 'main';
  const environments = useMemo(
    () => formData.environments || [],
    [formData.environments],
  );
  const environmentConfigs = useMemo(
    () => formData.environmentConfigs || {},
    [formData.environmentConfigs],
  );

  // Remove domains restored from an older draft now that the form uses IP:port.
  useEffect(() => {
    if (
      !Object.values(environmentConfigs).some(cfg => cfg.domain !== undefined)
    )
      return;
    onChange({
      ...formData,
      environmentConfigs: Object.fromEntries(
        Object.entries(environmentConfigs).map(
          ([name, { domain: _domain, ...cfg }]) => [name, cfg],
        ),
      ),
    });
  }, [environmentConfigs, formData, onChange]);

  // 4. Tab selection for Environment Configuration
  const [activeEnvTab, setActiveEnvTab] = useState<number>(0);

  // 5. New tag input state
  const [tagInput, setTagInput] = useState('');
  const [tagInputError, setTagInputError] = useState<string | null>(null);

  // 6. Pre-check Preview state
  const [checkingPreview, setCheckingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<{
    sourceSha?: string;
    branches: BranchPreviewItem[];
  } | null>(null);

  // Load Catalog components, servers, users, and defaults
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        setLoadingComponents(true);
        setComponentError(null);

        const res = await catalogApi.getEntities({
          filter: { kind: 'Component' },
        });
        if (!mounted) return;

        const eligible = res.items.filter(
          c => extractRepoFromComponent(c) !== null,
        );
        setComponents(eligible);

        // Fetch servers, users, and defaults asynchronously from template-provisioning router
        discoveryApi
          .getBaseUrl('template-provisioning')
          .then(baseUrl => {
            fetchApi
              .fetch(`${baseUrl}/servers?environment=all`)
              .then(r => r.json())
              .then(d => {
                if (mounted && d?.items) setServers(d.items);
              })
              .catch(() => {});
            fetchApi
              .fetch(`${baseUrl}/users-with-github`)
              .then(r => r.json())
              .then(d => {
                if (mounted && d?.items) setUsers(d.items);
              })
              .catch(() => {});
            fetchApi
              .fetch(`${baseUrl}/defaults`)
              .then(r => r.json())
              .then(d => {
                if (mounted && d?.imageHub) setImageHub(d.imageHub);
              })
              .catch(() => {});
          })
          .catch(() => {});

        // Auto select first component if none selected
        if (!selectedComponentRef && eligible.length > 0) {
          const first = eligible[0];
          const ref = `component:${first.metadata.namespace || 'default'}/${
            first.metadata.name
          }`;
          const repo = extractRepoFromComponent(first);
          onChange({
            ...formData,
            componentRef: ref,
            componentName: (first.metadata.title ||
              first.metadata.name) as string,
            componentOwner: (first.spec?.owner || '') as string,
            repository: repo ? `${repo.owner}/${repo.repo}` : undefined,
            sourceBranch: formData.sourceBranch || 'main',
            environments: formData.environments || [],
            environmentConfigs: formData.environmentConfigs || {},
          });
        }
      } catch (err: any) {
        if (mounted) {
          setComponentError(
            err.message || 'Failed to load components from Catalog',
          );
        }
      } finally {
        if (mounted) setLoadingComponents(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, [catalogApi, discoveryApi, fetchApi]);

  // Selected component entity object
  const selectedEntity = useMemo(() => {
    return components.find(c => {
      const ref = `component:${c.metadata.namespace || 'default'}/${
        c.metadata.name
      }`;
      return (
        ref === selectedComponentRef || c.metadata.name === selectedComponentRef
      );
    });
  }, [components, selectedComponentRef]);

  const selectedRepo = useMemo(() => {
    if (!selectedEntity) return null;
    return extractRepoFromComponent(selectedEntity);
  }, [selectedEntity]);

  const componentName = useMemo(() => {
    return selectedEntity ? selectedEntity.metadata.name : 'app';
  }, [selectedEntity]);

  // Pre-check Preview function
  const checkBranchesPreview = useCallback(async () => {
    if (!selectedRepo) return;
    try {
      setCheckingPreview(true);
      setPreviewError(null);

      const baseUrl = await discoveryApi.getBaseUrl('template-provisioning');
      const res = await fetchApi.fetch(`${baseUrl}/environments/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repository: `${selectedRepo.owner}/${selectedRepo.repo}`,
          sourceBranch,
          environments,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.valid === false) {
        setPreviewError(
          data.error || `Failed branch pre-check (HTTP ${res.status})`,
        );
        setPreviewResult(null);
      } else {
        setPreviewResult({
          sourceSha: data.sourceSha,
          branches: data.branches || [],
        });
      }
    } catch (err: any) {
      setPreviewError(err.message || 'Error checking branches from GitHub');
      setPreviewResult(null);
    } finally {
      setCheckingPreview(false);
    }
  }, [discoveryApi, fetchApi, selectedRepo, sourceBranch, environments]);

  useEffect(() => {
    if (selectedRepo && environments.length > 0) {
      const timer = setTimeout(() => {
        checkBranchesPreview();
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [selectedRepo, sourceBranch, environments, checkBranchesPreview]);

  // Component change handler
  const handleComponentChange = (ref: string) => {
    const entity = components.find(
      c =>
        `component:${c.metadata.namespace || 'default'}/${c.metadata.name}` ===
        ref,
    );
    const repo = entity ? extractRepoFromComponent(entity) : null;

    onChange({
      ...formData,
      componentRef: ref,
      projectAdminRefs: [],
      componentName: entity
        ? ((entity.metadata.title || entity.metadata.name) as string)
        : undefined,
      componentOwner: entity
        ? ((entity.spec?.owner || '') as string)
        : undefined,
      repository: repo ? `${repo.owner}/${repo.repo}` : undefined,
      sourceBranch,
      environments,
      environmentConfigs,
    });
  };

  // Source branch change handler
  const handleSourceBranchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({
      ...formData,
      sourceBranch: val,
    });
  };

  // Helper to create default config for an environment
  const createDefaultConfigForEnv = useCallback(
    (tag: string): EnvironmentConfigItem => {
      const isProd = tag === 'production' || tag === 'prod';
      const defaultServer = isProd
        ? servers.find(s => s.environment === 'product')?.id || 'prod-server-01'
        : servers.find(s => s.environment === 'staging')?.id ||
          'staging-server-01';

      return {
        runtime: 'compose',
        serverId: defaultServer,
        execution: 'setup',
        cpus: 0.5,
        memory: '1g',
        internalPort: 3000,
        externalPort: 3000,
        allowedUsers: [],
        ...(isProd ? { healthCheckUrl: '/health' } : {}),
      };
    },
    [servers],
  );

  // Environment tag add handler
  const handleAddTag = (rawTag: string) => {
    const tag = rawTag.trim();
    if (!tag) return;

    if (tag === sourceBranch) {
      setTagInputError(
        `Environment branch cannot be identical to source branch "${sourceBranch}"`,
      );
      return;
    }

    const validation = validateGitBranchName(tag);
    if (!validation.valid) {
      setTagInputError(validation.error || 'Invalid Git branch name');
      return;
    }

    if (environments.includes(tag)) {
      setTagInputError(`Branch "${tag}" has already been added`);
      return;
    }

    setTagInputError(null);
    setTagInput('');
    const updated = [...environments, tag];
    const updatedConfigs = {
      ...environmentConfigs,
      [tag]: environmentConfigs[tag] || createDefaultConfigForEnv(tag),
    };

    onChange({
      ...formData,
      environments: updated,
      environmentConfigs: updatedConfigs,
    });

    setActiveEnvTab(updated.length - 1);
  };

  const handleKeyDownTag = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updated = environments.filter(t => t !== tagToRemove);
    const updatedConfigs = { ...environmentConfigs };
    delete updatedConfigs[tagToRemove];

    onChange({
      ...formData,
      environments: updated,
      environmentConfigs: updatedConfigs,
    });

    if (activeEnvTab >= updated.length) {
      setActiveEnvTab(Math.max(0, updated.length - 1));
    }
  };

  // Update configuration for a specific environment
  const updateEnvConfig = (
    tag: string,
    updates: Partial<EnvironmentConfigItem>,
  ) => {
    const current = environmentConfigs[tag] || createDefaultConfigForEnv(tag);
    const updatedConfigs = {
      ...environmentConfigs,
      [tag]: {
        ...current,
        ...updates,
      },
    };

    onChange({
      ...formData,
      environmentConfigs: updatedConfigs,
    });
  };

  const currentActiveTag = environments[activeEnvTab] || environments[0];
  const currentEnvConfig = currentActiveTag
    ? environmentConfigs[currentActiveTag] ||
      createDefaultConfigForEnv(currentActiveTag)
    : undefined;

  const isCurrentProd =
    currentActiveTag === 'production' || currentActiveTag === 'prod';

  // Compose preview for current active environment
  const currentComposeYaml = useMemo(() => {
    if (!currentEnvConfig) return '';
    return generatePreviewComposeYaml({
      imageHub,
      component: componentName,
      externalPort: currentEnvConfig.externalPort ?? 3000,
      internalPort: currentEnvConfig.internalPort || 3000,
      cpus: currentEnvConfig.cpus || 0.5,
      memory: currentEnvConfig.memory || '1g',
    });
  }, [currentEnvConfig, imageHub, componentName]);

  const composePath = currentEnvConfig?.deployDirectory
    ? `${currentEnvConfig.deployDirectory}/docker-compose.yml`
    : `/home/abc_workspace/${
        selectedRepo?.repo || componentName
      }/${currentActiveTag}/docker-compose.yml`;

  const theme = useTheme();
  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <Box marginY={1} style={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
      {hasErrors && (
        <Box mb={2}>
          <Alert severity="error">{rawErrors[0]}</Alert>
        </Box>
      )}

      {/* 1. Component Selection */}
      <Paper
        variant="outlined"
        style={{
          padding: 18,
          marginBottom: 18,
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          <GitHubIcon color="action" style={{ marginRight: 8 }} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            1. Select Project Component
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" paragraph>
          Select a project Component created in the Catalog. Only components
          with valid GitHub repositories are eligible.
        </Typography>

        {loadingComponents ? (
          <Box display="flex" alignItems="center" p={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginLeft: 12 }}>
              Loading Catalog Components...
            </Typography>
          </Box>
        ) : componentError ? (
          <Alert severity="error">{componentError}</Alert>
        ) : components.length === 0 ? (
          <Alert severity="warning">
            No components with valid GitHub repositories found in the Catalog.
          </Alert>
        ) : (
          <Autocomplete
            id="component-select"
            options={components}
            value={selectedEntity || null}
            fullWidth
            getOptionSelected={(option, value) =>
              option.metadata.name === value.metadata.name &&
              option.metadata.namespace === value.metadata.namespace
            }
            getOptionLabel={option =>
              `${option.metadata.title || option.metadata.name} (${option.metadata.namespace || 'default'}/${option.metadata.name})`
            }
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                `${option.metadata.title || ''} ${option.metadata.name} ${option.metadata.namespace || 'default'} ${JSON.stringify(extractRepoFromComponent(option))}`
                  .toLowerCase()
                  .includes(inputValue.trim().toLowerCase()),
              )
            }
            onChange={(_, entity) => {
              handleComponentChange(
                entity
                  ? `component:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`
                  : '',
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                margin="dense"
                label="Catalog Component"
                placeholder="Search projects by name or repository"
                helperText="Select the target Component to setup environment branches"
              />
            )}
          />
        )}
        <Autocomplete
          multiple
          fullWidth
          id="project-admins"
          options={users.map(user => user.entityRef)}
          value={formData.projectAdminRefs || []}
          disabled={!selectedEntity}
          limitTags={3}
          renderTags={(value, getTagProps) =>
            value.map((ref, index) => (
              <Chip
                {...getTagProps({ index })}
                key={ref}
                size="small"
                style={{ maxWidth: '100%' }}
                label={users.find(user => user.entityRef === ref)?.email || ref}
              />
            ))
          }
          getOptionLabel={ref => {
            const user = users.find(candidate => candidate.entityRef === ref);
            return user ? `${user.displayName} (${user.email || ref})` : ref;
          }}
          onChange={(_, projectAdminRefs) =>
            onChange({ ...formData, projectAdminRefs })
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              margin="normal"
              label="Project admins"
              placeholder="Invite admins by name or email"
              helperText="Invite additional admins to manage all project environments, pipelines, environment variables and collaborators. Existing admins retain access."
            />
          )}
        />
      </Paper>

      {/* 2. Source Branch & Environment Tags */}
      <Paper
        variant="outlined"
        style={{
          padding: 18,
          marginBottom: 18,
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          <DnsIcon color="action" style={{ marginRight: 8 }} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            2. Source Branch & Target Environments
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" paragraph>
          Specify the source branch (commit SHA will be captured) and add
          environment tags to provision.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box height={32} />
            <TextField
              id="source-branch-input"
              label="Source Branch"
              value={sourceBranch}
              onChange={handleSourceBranchChange}
              variant="outlined"
              margin="dense"
              fullWidth
              helperText="Branch to branch off from (default: main)."
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {/* Quick Add Presets */}
            <Box
              minHeight={32}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginRight: 8 }}
              >
                Quick add:
              </Typography>
              {['staging', 'production', 'preprod', 'qa'].map(preset => (
                <Chip
                  key={preset}
                  label={`+ ${preset}`}
                  size="small"
                  clickable
                  disabled={environments.includes(preset)}
                  onClick={() => handleAddTag(preset)}
                  style={{ marginRight: 6, marginBottom: 4 }}
                />
              ))}
            </Box>

            {/* Input for custom tag */}
            <Box display="flex" alignItems="flex-start">
              <TextField
                id="environment-tag-input"
                label="New Environment Tag"
                placeholder="e.g. staging or preprod"
                value={tagInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setTagInput(e.target.value);
                  setTagInputError(null);
                }}
                onKeyDown={handleKeyDownTag}
                variant="outlined"
                margin="dense"
                error={Boolean(tagInputError)}
                helperText={tagInputError || 'Press Enter or click Add'}
                style={{ flexGrow: 1, marginRight: 8 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddTag(tagInput)}
                style={{ marginTop: 8, height: 40, flexShrink: 0 }}
              >
                Add
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Active Tags */}
        <Box mt={2} display="flex" flexWrap="wrap" alignItems="center">
          {environments.length === 0 ? (
            <Typography variant="caption" color="error">
              No environment tags specified. Please add at least one
              environment.
            </Typography>
          ) : (
            environments.map(tag => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                color="primary"
                variant="outlined"
                style={{
                  marginRight: 8,
                  marginBottom: 8,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              />
            ))
          )}
        </Box>
      </Paper>

      {/* 3. Environment Configuration (Compose, Server, Limits, Users) */}
      {environments.length > 0 && currentActiveTag && currentEnvConfig && (
        <Paper
          variant="outlined"
          style={{
            padding: 18,
            marginBottom: 18,
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Box display="flex" alignItems="center">
              <SettingsIcon color="action" style={{ marginRight: 8 }} />
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                3. Configure Environment Infrastructure & Compose
              </Typography>
            </Box>
            {isCurrentProd && (
              <Chip
                label="Production Environment (Strict Policy)"
                color="secondary"
                size="small"
                style={{ fontWeight: 600 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" paragraph>
            Configure server, resource limits (CPU/RAM), internal/external
            ports, and allowed users for each environment.
          </Typography>

          {/* Environment Tabs */}
          <Tabs
            value={activeEnvTab}
            onChange={(_, newVal) => setActiveEnvTab(newVal)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            style={{ marginBottom: 16, borderBottom: '1px solid #e0e0e0' }}
          >
            {environments.map(env => (
              <Tab
                key={env}
                label={env}
                style={{ fontWeight: 600, textTransform: 'none' }}
              />
            ))}
          </Tabs>

          <Grid container spacing={3}>
            {/* Provisioning Action (Execution) */}
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                margin="dense"
                style={{
                  width: '100%',
                  padding: 12,
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                }}
              >
                <FormLabel
                  component="legend"
                  style={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginBottom: 6,
                    fontSize: '0.9rem',
                  }}
                >
                  Execution Action for &ldquo;{currentActiveTag}&rdquo;
                </FormLabel>
                <RadioGroup
                  row
                  aria-label={`execution-action-${currentActiveTag}`}
                  name={`execution-${currentActiveTag}`}
                  value={currentEnvConfig.execution || 'setup'}
                  onChange={e =>
                    updateEnvConfig(currentActiveTag, {
                      execution: e.target.value as 'setup' | 'setup-and-deploy',
                    })
                  }
                >
                  <FormControlLabel
                    value="setup"
                    control={
                      <Radio
                        color="primary"
                        id={`radio-setup-${currentActiveTag}`}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          Setup (Default)
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Prepare branch, CI/CD files, Compose, and Jenkins job;
                          no build or deploy executed.
                        </Typography>
                      </Box>
                    }
                    style={{ marginRight: 32, alignItems: 'flex-start' }}
                  />
                  <FormControlLabel
                    value="setup-and-deploy"
                    control={
                      <Radio
                        color="primary"
                        id={`radio-setup-deploy-${currentActiveTag}`}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          Setup and Deploy
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Performs Setup then invokes deploy job to build image,
                          and run Docker Compose. Domain is saved as
                          configuration.
                        </Typography>
                      </Box>
                    }
                    style={{ alignItems: 'flex-start' }}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={currentEnvConfig.autoBuildOnPush === true}
                    onChange={e =>
                      updateEnvConfig(currentActiveTag, {
                        autoBuildOnPush: e.target.checked,
                      })
                    }
                  />
                }
                label="Automatically build and deploy when code is pushed to this branch (create GitHub webhook)"
              />
            </Grid>
            {/* Expected Access URL */}
            <Grid item xs={12} md={6}>
              <Box
                p={1.5}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary,
                  minHeight: 56,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Expected Access URL &mdash;{' '}
                  {currentEnvConfig.execution === 'setup-and-deploy' ? (
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                      Active after deploy
                    </span>
                  ) : (
                    <span style={{ color: '#e65100', fontWeight: 600 }}>
                      Configured / Not deployed
                    </span>
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    fontFamily:
                      'Consolas, Monaco, "Courier New", Courier, monospace',
                    fontSize: '0.9rem',
                    marginTop: 2,
                    color: '#0f172a',
                  }}
                >
                  {computeExpectedUrl(
                    undefined,
                    servers.find(s => s.id === currentEnvConfig.serverId)?.ip,
                    currentEnvConfig.externalPort ?? 3000,
                  )}
                </Typography>
              </Box>
            </Grid>

            {/* Server Selection */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                margin="dense"
                label="Runtime"
                value={currentEnvConfig.runtime || 'compose'}
                onChange={event =>
                  updateEnvConfig(currentActiveTag, {
                    runtime: event.target.value as 'compose' | 'kubernetes',
                    externalPort:
                      event.target.value === 'kubernetes' ? 0 : 3000,
                    serverId: '',
                    serverName: undefined,
                    serverIp: undefined,
                  })
                }
              >
                <MenuItem value="compose">Docker Compose</MenuItem>
                <MenuItem value="kubernetes">Kubernetes</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel id={`server-label-${currentActiveTag}`}>
                  Target Server
                </InputLabel>
                <Select
                  labelId={`server-label-${currentActiveTag}`}
                  id={`server-select-${currentActiveTag}`}
                  value={currentEnvConfig.serverId || ''}
                  onChange={e => {
                    const sId = e.target.value as string;
                    const matched = servers.find(s => s.id === sId);
                    updateEnvConfig(currentActiveTag, {
                      serverId: sId,
                      serverName: matched?.name,
                      serverIp: matched?.ip,
                    });
                  }}
                  label="Target Server"
                >
                  {servers
                    .filter(s =>
                      s.runtimes.includes(
                        currentEnvConfig.runtime || 'compose',
                      ),
                    )
                    .map(s => {
                      const envMismatch =
                        (isCurrentProd && s.environment === 'staging') ||
                        (!isCurrentProd && s.environment === 'product');

                      return (
                        <MenuItem
                          key={s.id}
                          value={s.id}
                          disabled={envMismatch}
                        >
                          <strong>{s.name}</strong> ({s.ip}) &mdash;{' '}
                          <span
                            style={{
                              color: envMismatch ? '#e53935' : '#43a047',
                            }}
                          >
                            [{s.environment.toUpperCase()}]
                          </span>
                        </MenuItem>
                      );
                    })}
                </Select>
                <FormHelperText>
                  Only servers supporting the selected runtime are shown.
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Docker Image Hub preview (Read-only) */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Docker Image Repository (Read-only)"
                value={`${imageHub}/${componentName.toLowerCase()}:\${IMAGE_TAG}`}
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
                helperText="Derived from platform IMAGE_HUB and Component name."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="SSH User"
                value={currentEnvConfig.sshUser ?? 'jenkins'}
                fullWidth
                variant="outlined"
                margin="dense"
                onChange={e =>
                  updateEnvConfig(currentActiveTag, { sshUser: e.target.value })
                }
                helperText="Uses Jenkins credential server-ssh-key. Host comes from selected server."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="SSH Port"
                type="number"
                value={currentEnvConfig.sshPort ?? 22}
                fullWidth
                variant="outlined"
                margin="dense"
                inputProps={{ min: 1, max: 65535 }}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    sshPort: Number(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Remote Deploy Directory"
                fullWidth
                variant="outlined"
                margin="dense"
                value={
                  currentEnvConfig.deployDirectory?.startsWith(
                    '/home/abc_workspace/',
                  )
                    ? currentEnvConfig.deployDirectory.slice(
                        '/home/abc_workspace/'.length,
                      )
                    : currentEnvConfig.deployDirectory === undefined
                    ? `${
                        selectedRepo?.repo || componentName
                      }/${currentActiveTag}`
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      /home/abc_workspace/
                    </InputAdornment>
                  ),
                }}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    deployDirectory: `/home/abc_workspace/${e.target.value.replace(
                      /^\/+/,
                      '',
                    )}`,
                  })
                }
                helperText="Base directory is fixed. Edit the repository folder or add subdirectories, e.g. repo/staging."
              />
            </Grid>
            {/* CPU & RAM Limits */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id={`cpu-input-${currentActiveTag}`}
                label="CPU Limit"
                type="number"
                inputProps={{ step: 0.1, min: 0.1, max: 32 }}
                value={currentEnvConfig.cpus}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    cpus: parseFloat(e.target.value) || 0.5,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                helperText="Allocated CPU cores (default: 0.5)"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id={`memory-input-${currentActiveTag}`}
                label="Memory Limit (RAM)"
                value={currentEnvConfig.memory}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    memory: e.target.value.trim(),
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                helperText="Format: 512m, 1g, 2g, 4g"
              />
            </Grid>

            {/* Port Settings */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id={`internal-port-${currentActiveTag}`}
                label="Internal Container Port"
                type="number"
                inputProps={{ min: 1, max: 65535 }}
                value={currentEnvConfig.internalPort}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    internalPort: parseInt(e.target.value, 10) || 3000,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                helperText="Port app listens on (1–65535)"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id={`external-port-${currentActiveTag}`}
                label="External Host Port"
                type="number"
                inputProps={{ min: 0, max: 65535 }}
                value={currentEnvConfig.externalPort}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    externalPort: Number(e.target.value),
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                required
                error={Boolean(
                  environments.some(env => {
                    const other = environmentConfigs[env];
                    const host =
                      servers.find(s => s.id === currentEnvConfig.serverId)
                        ?.ip || currentEnvConfig.serverId;
                    const otherHost =
                      servers.find(s => s.id === other?.serverId)?.ip ||
                      other?.serverId;
                    return (
                      env !== currentActiveTag &&
                      other &&
                      host === otherHost &&
                      currentEnvConfig.externalPort > 0 &&
                      other.externalPort === currentEnvConfig.externalPort
                    );
                  }),
                )}
                helperText={
                  environments.some(env => {
                    const other = environmentConfigs[env];
                    const host =
                      servers.find(s => s.id === currentEnvConfig.serverId)
                        ?.ip || currentEnvConfig.serverId;
                    const otherHost =
                      servers.find(s => s.id === other?.serverId)?.ip ||
                      other?.serverId;
                    return (
                      env !== currentActiveTag &&
                      other &&
                      host === otherHost &&
                      currentEnvConfig.externalPort > 0 &&
                      other.externalPort === currentEnvConfig.externalPort
                    );
                  })
                    ? 'This host port is already assigned to another environment on the same server IP.'
                    : 'Host port (1–65535); 0 disables external publishing. Jenkins checks live listeners before deployment.'
                }
              />
            </Grid>

            {/* Health Check URL (Required for production) */}
            <Grid item xs={12} md={6}>
              <TextField
                id={`health-check-${currentActiveTag}`}
                label={
                  isCurrentProd
                    ? 'Health Check URL (Required for Production)'
                    : 'Health Check URL (Optional)'
                }
                value={currentEnvConfig.healthCheckUrl || ''}
                onChange={e =>
                  updateEnvConfig(currentActiveTag, {
                    healthCheckUrl: e.target.value.trim(),
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                required={isCurrentProd}
                error={isCurrentProd && !currentEnvConfig.healthCheckUrl}
                helperText={
                  isCurrentProd
                    ? 'Mandatory for production deployments (e.g. /health or /api/health)'
                    : 'Endpoint used to verify application readiness after deployment'
                }
              />
            </Grid>

            {/* Allowed Jenkins Users */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                id={`allowed-users-${currentActiveTag}`}
                options={users.filter(u => u.email).map(u => u.entityRef)}
                getOptionLabel={ref => {
                  const user = users.find(u => u.entityRef === ref);
                  return user ? `${user.displayName} (${user.email})` : ref;
                }}
                value={currentEnvConfig.allowedUsers || []}
                onChange={(_, newValue) =>
                  updateEnvConfig(currentActiveTag, {
                    allowedUsers: newValue,
                  })
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      style={{ maxWidth: '100%' }}
                      size="small"
                      label={
                        users.find(u => u.entityRef === option)?.email || option
                      }
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    margin="dense"
                    label="Environment members"
                    placeholder="Select employees by name or email"
                    helperText="Leave empty to inherit project invitees, or select members for this environment"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Docker Compose YAML Live Preview Box */}
          <Box mt={3}>
            <Box display="flex" alignItems="center" mb={1}>
              <CodeIcon
                fontSize="small"
                style={{ marginRight: 6, color: '#0366d6' }}
              />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {currentEnvConfig.runtime === 'kubernetes'
                  ? 'Kubernetes configuration preview'
                  : 'Live Docker Compose Preview'}{' '}
                &mdash;{' '}
                <code>
                  {currentEnvConfig.runtime === 'kubernetes'
                    ? composePath.replace(
                        /docker-compose\.yml$/,
                        'kubernetes.json',
                      )
                    : composePath}
                </code>{' '}
                (VPS)
              </Typography>
            </Box>
            <Paper
              style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: 14,
                borderRadius: 6,
                fontFamily:
                  'Consolas, Monaco, "Courier New", Courier, monospace',
                fontSize: '0.85rem',
                overflowX: 'auto',
                border: '1px solid #333',
              }}
            >
              <pre style={{ margin: 0 }}>
                {currentEnvConfig.runtime === 'kubernetes'
                  ? JSON.stringify(
                      {
                        runtime: 'kubernetes',
                        image: `${imageHub}/${componentName.toLowerCase()}:IMAGE_TAG`,
                        namespace: `${componentName}-${currentActiveTag}`
                          .toLowerCase()
                          .replace(/_/g, '-'),
                        resources: {
                          cpus: currentEnvConfig.cpus,
                          memory: currentEnvConfig.memory,
                        },
                        containerPort: currentEnvConfig.internalPort,
                        service: currentEnvConfig.externalPort
                          ? {
                              type: 'NodePort',
                              nodePort: currentEnvConfig.externalPort,
                            }
                          : { type: 'ClusterIP' },
                        domain: currentEnvConfig.domain || undefined,
                      },
                      null,
                      2,
                    )
                  : currentComposeYaml}
              </pre>
            </Paper>
          </Box>
        </Paper>
      )}

      {/* 4. Pre-check & Review Summary */}
      <Paper
        variant="outlined"
        style={{
          padding: 18,
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            4. Review & Pre-check Summary
          </Typography>
          <Button
            size="small"
            startIcon={
              checkingPreview ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            disabled={checkingPreview || !selectedRepo}
            onClick={checkBranchesPreview}
          >
            Re-check
          </Button>
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          Review configuration across all environments. Branches and Jenkins
          jobs will be provisioned upon submitting the Scaffolder task.
        </Typography>

        {previewError && (
          <Box my={1}>
            <Alert severity="error">{previewError}</Alert>
          </Box>
        )}

        {previewResult && (
          <Box mt={1}>
            <Box
              display="flex"
              alignItems="center"
              p={1.5}
              mb={2}
              style={{
                backgroundColor: '#edf7ed',
                border: '1px solid #c8e6c9',
                borderRadius: 4,
              }}
            >
              <CheckCircleIcon style={{ color: '#2e7d32', marginRight: 8 }} />
              <Typography
                variant="body2"
                style={{ color: '#1b5e20', fontWeight: 500 }}
              >
                Source branch <strong>{sourceBranch}</strong> verified on
                GitHub. Latest commit SHA:{' '}
                <code>{previewResult.sourceSha?.substring(0, 7)}</code>
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              style={{ marginBottom: 16 }}
            >
              <Table size="small" style={{ minWidth: 960 }}>
                <TableHead style={{ backgroundColor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>
                      Environment / Branch
                    </TableCell>
                    <TableCell style={{ fontWeight: 600 }}>
                      Pre-check Status
                    </TableCell>
                    <TableCell style={{ fontWeight: 600 }}>
                      Action to be taken
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewResult.branches.map(b => (
                    <TableRow key={b.name}>
                      <TableCell>
                        <code style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                          {b.name}
                        </code>
                      </TableCell>
                      <TableCell>
                        {b.status === 'Will be created' ? (
                          <Chip
                            label="Will be created"
                            size="small"
                            style={{
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                            }}
                          />
                        ) : b.status ===
                          'Already exists — will be preserved' ? (
                          <Chip
                            label="Already exists — will be preserved"
                            size="small"
                            style={{
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Check failed"
                            size="small"
                            style={{
                              backgroundColor: '#ffebee',
                              color: '#c62828',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {b.status === 'Will be created'
                            ? `Will create ref refs/heads/${
                                b.name
                              } pointing to ${previewResult.sourceSha?.substring(
                                0,
                                7,
                              )}`
                            : 'Will preserve existing branch and all commits intact (no overwrite)'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Configuration Review Table */}
        {environments.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            style={{ marginTop: 12 }}
          >
            <Table size="small">
              <TableHead style={{ backgroundColor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Environment</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Server</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>
                    Expected Access URL
                  </TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Resources</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>
                    Ports (Host:App)
                  </TableCell>
                  <TableCell style={{ fontWeight: 600 }}>
                    Expected Job
                  </TableCell>
                  <TableCell style={{ fontWeight: 600 }}>
                    Pre-check Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {environments.map(env => {
                  const cfg =
                    environmentConfigs[env] || createDefaultConfigForEnv(env);
                  const branchStatus = previewResult?.branches.find(
                    b => b.name === env,
                  );

                  return (
                    <TableRow key={env}>
                      <TableCell>
                        <code style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                          {env}
                        </code>
                      </TableCell>
                      <TableCell>{cfg.serverId}</TableCell>
                      <TableCell>
                        {cfg.execution === 'setup-and-deploy' ? (
                          <Chip
                            label="Setup &amp; Deploy"
                            size="small"
                            style={{
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Setup only"
                            size="small"
                            style={{
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="caption"
                            display="block"
                            style={{
                              fontFamily:
                                'Consolas, Monaco, "Courier New", Courier, monospace',
                              color: '#0366d6',
                            }}
                          >
                            {computeExpectedUrl(
                              undefined,
                              servers.find(s => s.id === cfg.serverId)?.ip,
                              cfg.externalPort,
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{
                              color:
                                cfg.execution === 'setup-and-deploy'
                                  ? '#2e7d32'
                                  : '#e65100',
                            }}
                          >
                            {cfg.execution === 'setup-and-deploy'
                              ? 'Deploy on apply'
                              : 'Configured / Not deployed'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {cfg.cpus} CPU / {cfg.memory}
                      </TableCell>
                      <TableCell>
                        <code>
                          {cfg.externalPort === 0
                            ? 'Disabled (internal only)'
                            : `${cfg.externalPort}:${cfg.internalPort}`}
                        </code>
                      </TableCell>
                      <TableCell>
                        <code>{`${componentName}-${env}`}</code>
                      </TableCell>
                      <TableCell>
                        {branchStatus?.status === 'Will be created' ? (
                          <Chip
                            label="Will be created"
                            size="small"
                            style={{
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                            }}
                          />
                        ) : branchStatus?.status ===
                          'Already exists — will be preserved' ? (
                          <Chip
                            label="Already exists — will be preserved"
                            size="small"
                            style={{
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Pending review"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export const devopsEnvironmentPickerValidation = (
  value: any,
  validation: any,
) => {
  if (!value || typeof value !== 'object') {
    validation.addError('DevOps environment configuration is required');
    return;
  }
  if (!value.componentRef) {
    validation.addError('Please select a target Catalog Component');
  }
  if (!value.repository) {
    validation.addError(
      'Target component does not have a valid GitHub repository annotation',
    );
  }
  if (!value.sourceBranch || !value.sourceBranch.trim()) {
    validation.addError('Source branch is required');
  } else {
    const srcVal = validateGitBranchName(value.sourceBranch);
    if (!srcVal.valid) {
      validation.addError(`Invalid source branch: ${srcVal.error}`);
    }
  }

  if (
    !value.environments ||
    !Array.isArray(value.environments) ||
    value.environments.length === 0
  ) {
    validation.addError('At least one environment tag must be specified');
  } else {
    const configs = value.environmentConfigs || {};
    const usedHostPorts = new Map<string, string>(); // serverId:externalPort -> env

    for (const env of value.environments) {
      if (env === value.sourceBranch) {
        validation.addError(
          `Environment branch "${env}" cannot match source branch "${value.sourceBranch}"`,
        );
      }
      const val = validateGitBranchName(env);
      if (!val.valid) {
        validation.addError(`Invalid branch name "${env}": ${val.error}`);
      }

      const cfg = configs[env];
      if (cfg) {
        if (cfg.sshUser && !/^[a-z_][a-z0-9_-]*$/.test(cfg.sshUser))
          validation.addError('SSH user is invalid');
        if (
          cfg.sshPort !== undefined &&
          (!Number.isInteger(cfg.sshPort) ||
            cfg.sshPort < 1 ||
            cfg.sshPort > 65535)
        )
          validation.addError('SSH port must be between 1 and 65535');
        if (
          cfg.deployDirectory &&
          (!/^\/[a-zA-Z0-9_./-]+$/.test(cfg.deployDirectory) ||
            cfg.deployDirectory.split('/').includes('..') ||
            !cfg.deployDirectory.startsWith('/home/abc_workspace/') ||
            !cfg.deployDirectory
              .slice('/home/abc_workspace/'.length)
              .split('/')
              .some((part: string) => part && part !== '.'))
        )
          validation.addError(
            'Deploy directory must contain a folder inside /home/abc_workspace/',
          );
        // Execution mode validation
        if (
          cfg.execution &&
          cfg.execution !== 'setup' &&
          cfg.execution !== 'setup-and-deploy'
        ) {
          validation.addError(
            `Environment "${env}": Execution mode must be "setup" or "setup-and-deploy"`,
          );
        }

        // Domain validation
        if (cfg.domain && cfg.domain.trim() !== '') {
          const domVal = validateDomainName(cfg.domain);
          if (!domVal.valid) {
            validation.addError(
              `Environment "${env}": Invalid domain format (${domVal.error})`,
            );
          }
        }

        if (!cfg.serverId || !cfg.serverId.trim()) {
          validation.addError(
            `Environment "${env}": Server selection is required`,
          );
        }

        if (typeof cfg.cpus !== 'number' || cfg.cpus <= 0 || cfg.cpus > 64) {
          validation.addError(
            `Environment "${env}": CPUs must be a positive number up to 64`,
          );
        }

        if (!cfg.memory || !/^\d+(?:[mMgG])$/.test(cfg.memory.trim())) {
          validation.addError(
            `Environment "${env}": Memory must be in valid format (e.g. 512m, 2g)`,
          );
        }

        if (
          !Number.isInteger(cfg.internalPort) ||
          cfg.internalPort < 1 ||
          cfg.internalPort > 65535
        ) {
          validation.addError(
            `Environment "${env}": Internal port must be between 1 and 65535`,
          );
        }

        if (
          !Number.isInteger(cfg.externalPort) ||
          cfg.externalPort < 0 ||
          cfg.externalPort > 65535
        ) {
          validation.addError(
            `Environment "${env}": External port must be between 0 and 65535 (0 disables host publishing)`,
          );
        }

        // Port conflict check across environments in same request
        if (cfg.serverId && cfg.externalPort) {
          const key = `${cfg.serverIp || cfg.serverId}:${cfg.externalPort}`;
          if (usedHostPorts.has(key)) {
            validation.addError(
              `Port conflict: Environment "${env}" uses port ${
                cfg.externalPort
              } on server "${
                cfg.serverId
              }", which is already assigned to "${usedHostPorts.get(key)}"`,
            );
          } else {
            usedHostPorts.set(key, env);
          }
        }

        // Production health check URL requirement
        if (
          (env === 'production' || env === 'prod') &&
          (!cfg.healthCheckUrl || !cfg.healthCheckUrl.trim())
        ) {
          validation.addError(
            `Environment "${env}": Health check URL is required for production`,
          );
        }
      }
    }

    // Check for duplicate domains across environments in the same form
    const usedDomains = new Map<string, string>(); // domain -> env
    for (const env of value.environments) {
      const cfg = configs[env];
      if (cfg && cfg.domain && cfg.domain.trim() !== '') {
        const normDomain = cfg.domain.trim().toLowerCase();
        if (usedDomains.has(normDomain)) {
          validation.addError(
            `Domain conflict: Environment "${env}" uses domain "${normDomain}", which is already assigned to "${usedDomains.get(
              normDomain,
            )}"`,
          );
        } else {
          usedDomains.set(normDomain, env);
        }
      }
    }
  }
};

export const DevopsEnvironmentPicker = createFormField({
  name: 'DevopsEnvironmentPicker',
  component: DevopsEnvironmentPickerComponent as any,
  validation: devopsEnvironmentPickerValidation as any,
});
